import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { locationPrice, hasCoordinates, type GasPriceRow } from "./gasPricesModel";
import { createGasBrandIcon } from "./gasBrands";
import { GAS_MARKER_WIDTH, GAS_MARKER_HEIGHT, layoutGasMarkers, type ExpandedMarkers } from "./gasMarkerLayout";

export function GasPricesMap({ rows, selected, onSelect }: { rows: GasPriceRow[]; selected: string; onSelect: (key: string) => void }) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const markers = useRef(new Map<string, L.Marker>());
  const reveal = useRef<(key: string) => void>(() => {});
  const updateExpansion = useRef<(page: number | null) => void>(() => {});
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const [tileError, setTileError] = useState(false);
  const [expansion, setExpansion] = useState({ count: 0, page: 0, pages: 0 });
  useEffect(() => {
    if (!container.current) return;
    const instance = L.map(container.current, { scrollWheelZoom: false }).setView([51, -96], 4);
    map.current = instance;
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).on("tileerror", () => setTileError(true)).addTo(instance);
    const observer = new ResizeObserver(() => instance.invalidateSize());
    observer.observe(container.current);
    return () => { observer.disconnect(); instance.remove(); map.current = null; };
  }, []);
  useEffect(() => {
    if (!map.current) return;
    const instance = map.current;
    const layer = L.layerGroup().addTo(instance);
    const locations = rows.filter(hasCoordinates);
    const byKey = new Map(locations.map(row => [row.key, row]));
    const bounds = L.latLngBounds(locations.map(row => [row.latitude!, row.longitude!] as L.LatLngTuple));
    let expanded: ExpandedMarkers | null = null;
    let frame = 0;
    const first = rows[0];
    const station = first && hasCoordinates({ latitude: first.stationLatitude, longitude: first.stationLongitude })
      ? L.latLng(first.stationLatitude!, first.stationLongitude!) : null;
    if (station) instance.setView(station, 14, { animate: false });
    else if (bounds.isValid()) instance.fitBounds(bounds, { padding: [65, 80], maxZoom: 13 });

    const render = () => {
      instance.closePopup();
      layer.clearLayers();
      markers.current.clear();
      const size = instance.getSize();
      const points = locations.map(row => ({ key: row.key, ...instance.latLngToContainerPoint([row.latitude!, row.longitude!]) }))
        .filter(p => p.x >= 0 && p.y >= 0 && p.x <= size.x && p.y <= size.y);
      const layout = layoutGasMarkers(points, size, expanded);
      setExpansion({ count: layout.expandedCount, page: layout.page, pages: layout.pageCount });
      for (const p of points) {
        const row = byKey.get(p.key)!;
        L.circleMarker([row.latitude!, row.longitude!], { radius: 3, color: "#142b20", fillColor: row.ours ? "#42e4b0" : "#e5f5ed", fillOpacity: 1, weight: 1.5, interactive: false }).addTo(layer);
      }
      for (const placement of layout.placements) {
        const { point, origin, members, cluster } = placement;
        const position = instance.containerPointToLatLng(L.point(point.x, point.y));
        const row = byKey.get(members[0].key)!;
        const isSelected = !cluster && row.key === selectedRef.current;
        const color = isSelected ? "#ffe18a" : row.ours && !cluster ? "#42e4b0" : "#e5f5ed";
        if (Math.hypot(point.x - origin.x, point.y - origin.y) > 2) {
          const actual = instance.containerPointToLatLng(L.point(origin.x, origin.y));
          // Dark casing keeps connectors visible on roads and terrain.
          L.polyline([actual, position], { color: "#152c23", weight: 4, opacity: .85, interactive: false }).addTo(layer);
          L.polyline([actual, position], { color, weight: 1.5, opacity: .95, interactive: false }).addTo(layer);
        }
        if (cluster) {
          const content = document.createElement("span");
          content.textContent = `${members.length} stations +`;
          const clusterMarker = L.marker(position, {
            title: `Show ${members.length} nearby fuel stations`,
            icon: L.divIcon({ className: "gas-price-cluster", html: content, iconSize: [GAS_MARKER_WIDTH, GAS_MARKER_HEIGHT], iconAnchor: [GAS_MARKER_WIDTH / 2, GAS_MARKER_HEIGHT / 2] })
          }).addTo(layer).on("click", () => { expanded = { key: members[0].key, page: 0, keys: members.map(p => p.key) }; render(); });
          clusterMarker.getElement()?.setAttribute("aria-label", `Show ${members.length} nearby fuel stations`);
          continue;
        }
        const actual: L.LatLngTuple = [row.latitude!, row.longitude!];
        L.circleMarker(actual, { radius: isSelected ? 5 : 3, color: "#142b20", fillColor: color, fillOpacity: 1, weight: 1.5, interactive: false }).addTo(layer);
        const label = document.createElement("span");
        label.className = "gas-marker-price";
        label.textContent = locationPrice(row);
        const markerContent = document.createElement("span");
        markerContent.className = "gas-marker-content";
        markerContent.append(createGasBrandIcon(row.name), label);
        const marker = L.marker(position, {
          title: `${row.name}: ${locationPrice(row)} ${row.comparable ? "CAD per litre" : "source units unverified"}`,
          zIndexOffset: isSelected ? 1000 : 0,
          icon: L.divIcon({ className: `gas-price-marker${row.ours ? " gas-price-marker--ours" : ""}${row.price === null ? " gas-price-marker--missing" : ""}${isSelected ? " gas-price-marker--selected" : ""}`, html: markerContent, iconSize: [GAS_MARKER_WIDTH, GAS_MARKER_HEIGHT], iconAnchor: [GAS_MARKER_WIDTH / 2, GAS_MARKER_HEIGHT / 2] })
        }).addTo(layer);
        marker.getElement()?.setAttribute("aria-label", `${row.name}: ${locationPrice(row)} ${row.comparable ? "CAD per litre" : "source units unverified"}`);
        const popup = document.createElement("div");
        const title = document.createElement("strong");
        title.textContent = `${row.name} · ${locationPrice(row)} ${row.comparable ? "CAD/L" : "(source units unverified; excluded from KPIs)"}`;
        const popupHeader = document.createElement("div");
        popupHeader.className = "gas-popup-brand";
        popupHeader.append(createGasBrandIcon(row.name), title);
        popup.append(popupHeader, document.createTextNode(row.address), document.createElement("br"), document.createTextNode(row.reportedAgo ? `Reported ${row.reportedAgo} at collection` : "Report age unavailable"));
        marker.bindPopup(popup, { autoPan: false }).on("click", () => onSelect(row.key));
        markers.current.set(row.key, marker);
      }
      if (station && first) {
        const text = document.createElement("span"); text.textContent = first.station;
        L.circleMarker(station, { radius: 8, color: "#fff", fillColor: "#19c89c", fillOpacity: 1, weight: 3 })
          .bindTooltip(text, { direction: "top" }).addTo(layer);
      }
      markers.current.get(selectedRef.current)?.openPopup();
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(render); };
    const collapse = () => { if (expanded) { expanded = null; render(); } };
    const keydown = (event: KeyboardEvent) => { if (event.key === "Escape") collapse(); };
    instance.on("moveend zoomend resize", schedule);
    instance.on("click", collapse);
    instance.getContainer().addEventListener("keydown", keydown);
    updateExpansion.current = page => { expanded = page === null ? null : expanded && { ...expanded, page }; render(); };
    reveal.current = key => {
      if (!key) { expanded = null; render(); return; }
      const row = byKey.get(key);
      if (!row) { expanded = null; render(); return; }
      if (!markers.current.has(key)) {
        expanded = { key, page: -1 };
      }
      instance.panTo([row.latitude!, row.longitude!], { animate: false });
      cancelAnimationFrame(frame);
      render();
      markers.current.get(key)?.openPopup();
    };
    render();
    return () => {
      cancelAnimationFrame(frame);
      instance.off("moveend zoomend resize", schedule).off("click", collapse);
      instance.getContainer().removeEventListener("keydown", keydown);
      layer.remove(); markers.current.clear(); reveal.current = () => {}; updateExpansion.current = () => {};
    };
  }, [rows, onSelect]);
  useEffect(() => { reveal.current(selected); }, [selected]);
  return <div className="gas-map-wrap">
    <div ref={container} className="gas-map" aria-label="Map of fuel prices; dots mark actual locations and lines connect displaced labels; equivalent records are in the price comparison table" />
    <div className="gas-map-layout-help">
      <span>Dots mark actual locations. Lines connect offset prices. Click a station group to expand.</span>
      {expansion.count > 0 && <div className="gas-map-expansion">
        <span role="status">{expansion.count} stations{expansion.pages > 1 ? ` · Page ${expansion.page + 1} of ${expansion.pages}` : " expanded"}</span>
        {expansion.pages > 1 && <>
          <button type="button" disabled={expansion.page === 0} onClick={() => updateExpansion.current(expansion.page - 1)}>Previous</button>
          <button type="button" disabled={expansion.page + 1 >= expansion.pages} onClick={() => updateExpansion.current(expansion.page + 1)}>Next</button>
        </>}
        <button type="button" onClick={() => updateExpansion.current(null)}>Collapse group</button>
      </div>}
    </div>
    {tileError && <p role="status">Basemap could not load. Prices remain available in the table.</p>}
  </div>;
}
